import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useAuthContext } from "../context/AuthContext";
import { useMasters } from "../context/MastersContext";
import { useAppConfig } from "../hooks/useAppConfig";
import { useCostCenterDisplay } from "../hooks/useCostCenterDisplay";
import { useDataHierarchyCompat } from "../hooks/useDataHierarchyCompat";
import { useNatureConfig } from "../hooks/useNatureConfig";
import { useCategories, useItems } from "../hooks/useSettingsData";
import { useTitleCase } from "../hooks/useTitleCase";
import { useConfirmDialog } from "../hooks/useConfirmDialog";
import { ExpenseFormValues, zodExpenseSchema } from "../schemas/expense/zodSchema";
import { calculateAmount } from "../utils/calculateAmount";
import { DataHierarchyValidator } from "../utils/dataHierarchyValidator";
import { INPUT_LIMITS, useSanitizedInput } from "../utils/inputSecurity";
import { NATURE_ALLOWED_TYPES } from "../utils/natureTypes";
import { postExpense } from "../utils/postExpense";
// 🆕 UX Improvement Utilities
import { getTodayDate, getSmartDefaults, saveLastUsedValues, getLastUsedCostCenter, getLastUsedCategory } from "../utils/formDefaults";
import { getFieldErrorMessage, getFieldHelperText } from "../utils/errorMessages";
import DateRangePicker from "./forms/DateRangePicker";
import DynamicPartyInput, { PartyData } from "./forms/DynamicPartyInput";
import FileUpload from "./forms/FileUpload";
import BillUpload from "./forms/BillUpload";
import GroupedCategoryDropdown from "./forms/GroupedCategoryDropdown";
import RadioGroup from "./ui/RadioGroup";
import BusinessTypeTabs, { BusinessType } from "./ui/BusinessTypeTabs";
import NatureTabs, { ExpenseNature } from "./ui/NatureTabs";
import ModuleSpecificFields from "./forms/ModuleSpecificFields";
import UnifiedAmountModule from "./forms/UnifiedAmountModule";
import BillReviewModal from "./BillReviewModal";
import {
  PrimaryButton,
  SecondaryButton
} from "./ui";
import { SearchableDropdown } from "./ui/SearchableDropdown";
import ConfirmDialog from "./ui/modals/ConfirmDialog";
import type { MatchedBillData } from "../types/billParser";
import { uploadExpenseAttachment } from "../utils/firebase/firebaseStorage";

type TypeOption = { id: string; name: string };
type UnitOption = { id: string; name: string; type: string };
type CostCenterOption = { id: string; name: string; type: string };
type CategoryOption = {
  id: string;
  name: string;
  type: string;
  costCenter: string;
  needsNameField?: boolean;
  supportsUnits?: boolean;
  unitLabel?: string;
};

interface Props {
  nature?: ExpenseNature;
  layout?: 'modern' | 'single-column' | 'auto';
  initialBusinessType?: BusinessType; // 🆕 Optional initial business type
}

const ModernDataEntryForm: React.FC<Props> = ({ 
  nature: propNature, 
  layout = "auto",
  initialBusinessType = "mill" 
}) => {
  // 🆕 Tab State Management - Business Type & Nature Selection
  const [businessType, setBusinessType] = useState<BusinessType>(() => {
    // Try to restore last-used business type from localStorage
    const saved = localStorage.getItem('lastUsedBusinessType');
    return (saved as BusinessType) || initialBusinessType;
  });

  const [nature, setNature] = useState<ExpenseNature>(() => {
    // If propNature is provided, use it; otherwise restore from localStorage or default to 'routine'
    if (propNature) return propNature;
    const savedNature = localStorage.getItem(`lastUsedNature_${businessType}`);
    return (savedNature as ExpenseNature) || 'routine';
  });

  // Save tab selections to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('lastUsedBusinessType', businessType);
    localStorage.setItem(`lastUsedNature_${businessType}`, nature);
  }, [businessType, nature]);

  // Handle business type tab change
  const handleBusinessTypeChange = (newType: BusinessType) => {
    setBusinessType(newType);
    // Restore last-used nature for this business type, or default to 'routine'
    const savedNature = localStorage.getItem(`lastUsedNature_${newType}`);
    setNature((savedNature as ExpenseNature) || 'routine');
  };

  // Handle nature tab change
  const handleNatureChange = (newNature: ExpenseNature) => {
    setNature(newNature);
  };

  // Hooks and state management
  const { user, roles, assignedUnits } = useAuthContext();
  const { types, isLoading: mastersLoading, isError: mastersError, error: mastersErrorObj } = useMasters(); // Keep types from masters, use compatibility layer for others
  const { 
    units, 
    costCenters, 
    categories,
    getFilteredOptions,
    validateComparison 
  } = useDataHierarchyCompat(); // 🆕 Use compatibility layer for all data and filtering
  
  // Safety check for units - fallback to empty array if undefined (memoized to prevent infinite loops)
  const safeUnits = useMemo(() => units || [], [units]);
  const safeCostCenters = useMemo(() => costCenters || [], [costCenters]);
  const safeCategories = useMemo(() => categories || [], [categories]);
  
  const toTitleCase = useTitleCase();
  const { config } = useAppConfig();
  const { config: natureCfg } = useNatureConfig(nature);
  const { getCostCenterOptionsForType } = useCostCenterDisplay();

  // Items hooks for stock functionality
  const { data: categoriesData = [] } = useCategories();
  const { data: items = [] } = useItems();
  const [filteredItems, setFilteredItems] = useState<any[]>([]);

  // Screen size detection for responsive layout
  const [screenSize, setScreenSize] = useState<'mobile' | 'desktop'>('desktop');

  useEffect(() => {
    const checkScreenSize = () => {
      setScreenSize(window.innerWidth < 1024 ? 'mobile' : 'desktop');
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Determine effective layout based on prop and screen size
  const effectiveLayout = useMemo(() => {
    if (layout === 'auto') {
      return screenSize === 'mobile' ? 'single-column' : 'modern';
    }
    return layout;
  }, [layout, screenSize]);

  // UI state
  const [loading, setLoading] = useState(false);
  const { confirm, dialogProps } = useConfirmDialog();

  // Bill Upload State
  const [showBillUpload, setShowBillUpload] = useState(false);
  const [billReviewOpen, setBillReviewOpen] = useState(false);
  const [parsedBillData, setParsedBillData] = useState<MatchedBillData | null>(null);
  const [uploadedBillFile, setUploadedBillFile] = useState<File | null>(null);

  // UI state
  const [showName, setShowName] = useState(false);
  const [showUnits, setShowUnits] = useState(false);
  const [dateFromValue, setDateFromValue] = useState<Date | null>(null);
  const [dateToValue, setDateToValue] = useState<Date | null>(null);
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);

  // Security hooks
  const descriptionSecurity = useSanitizedInput('DESCRIPTION', INPUT_LIMITS.DESCRIPTION);
  const nameSecurity = useSanitizedInput('NAME', INPUT_LIMITS.NAME);

  // Form configuration
  const visibleFields = new Set(
    natureCfg?.fields.map(f => f.name) || ["shop", "description", "name"]
  );
  const requiredFields = new Set(
    natureCfg?.fields.filter(f => f.required).map(f => f.name) || []
  );

  // Check if date range should be shown
  const showDateRange = ["capital", "admin-finance", "payroll"].includes(nature);

  // 🆕 Form initialization with smart defaults
  const methods = useForm<any>({
    resolver: zodResolver(zodExpenseSchema),
    mode: "onBlur",
    defaultValues: {
      type: "",
      unit: "",
      costCenter: "",
      category: "",
      shop: "", // Deprecated: kept for backwards compatibility
      partyName: "",
      partyType: undefined,
      partyId: "",
      amount: "",
      quantity: "",
      rate: "",
      gross: "",
      taxAmount: "",
      gstAmount: "",
      enableGst: false,
      // Default amount mode: use quantity × rate for stock by default
      mode: nature === 'stock' ? "unit" : "total",
      date: getTodayDate(), // 🆕 Auto-fill today's date
      dateFrom: "",
      dateTo: "",
      description: "",
      name: "",
      showName: false,
      stockItem: "", // Add stockItem field
      approvalStatus: "pending",
      approvalComments: "",
      itemName: "",
      isConsumable: false,
      unitsSpent: "",
      balance: "",
      payoutMode: undefined,
      payoutFrequency: undefined,
      recurring: false,
      dueDate: "",
      invoiceNo: "",
      nature: nature,
      attachment: null,
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors, isValid, isSubmitting },
  } = methods;

  // Watch form values
  const formType = watch("type");
  const formCostCenter = watch("costCenter");
  const formCategory = watch("category");
  const formStockItem = watch("stockItem"); // Watch selected stock item
  const selectedCategory = categoriesData.find(c => c.id === formCategory); // Use categoriesData for supportsItems
  const selectedItem = items.find(item => item.id === formStockItem); // Find selected item
  const modeWatch = watch("mode");
  const qtyWatch = watch("quantity");
  const rateWatch = watch("rate");
  const grossWatch = watch("gross");
  const taxWatch = watch("taxAmount");
  const gstAmountWatch = watch("gstAmount");
  const enableGstWatch = watch("enableGst");
  const dateFromWatch = watch("dateFrom");
  const dateToWatch = watch("dateTo");
  const formUnit = watch("unit");
  const formAmount = watch("amount");
  const formDate = watch("date");
  const formDescription = watch("description"); // 🆕 Watch description for character count

  // 🔍 DEBUG: Enhanced form validation with hierarchy validation (memoized to prevent infinite loops)
  useEffect(() => {
    // Only log when core form values change, not on every render
    if (formType || formUnit || formCostCenter || formCategory) {
      // Validate form using hierarchy rules
      const hierarchyValidation = DataHierarchyValidator.validateFormSelection(
        formType,
        formUnit,
        formCostCenter,
        formCategory,
        safeUnits,
        safeCostCenters,
        safeCategories
      );

      // Get form values for debugging - use already watched values
      const formValues = {
        type: formType,
        unit: formUnit,
        costCenter: formCostCenter,
        category: formCategory,
        amount: formAmount,
        date: formDate
      };
      const requiredFields = ['type', 'unit', 'costCenter', 'category', 'amount', 'date'] as const;
      const missingFields = requiredFields.filter(field => !formValues[field as keyof typeof formValues]);
    }
  }, [formType, formUnit, formCostCenter, formCategory]);

  // Cascading selects logic
  const allowedTypeNames = NATURE_ALLOWED_TYPES[nature] || [];
  const allowedTypeIds = types
    .filter(t => allowedTypeNames.includes(t.name))
    .map(t => t.id);
  const isTypeFixed = allowedTypeIds.length === 1;

  // 🆕 AUTO-SET TYPE: Based on business type tab selection (Mill/Vehicle)
  useEffect(() => {
    // Find the type ID that matches the selected business type
    const businessTypeMap: Record<BusinessType, string> = {
      mill: 'Mill',
      vehicle: 'Vehicle'
    };
    
    const targetTypeName = businessTypeMap[businessType];
    const targetType = types.find(t => t.name === targetTypeName);
    
    if (targetType && allowedTypeIds.includes(targetType.id)) {
      // Get current form type value directly to avoid dependency
      const currentType = getValues("type");
      // Only update if different to avoid unnecessary re-renders
      if (currentType !== targetType.id) {
        setValue("type", targetType.id);
        // Reset dependent fields
        setValue("unit", "");
        setValue("costCenter", "");
        setValue("category", "");
      }
    }
  }, [businessType, types, allowedTypeIds, getValues, setValue]);

  // Auto-select type when only one allowed (legacy support)
  useEffect(() => {
    if (isTypeFixed && allowedTypeIds[0] && formType !== allowedTypeIds[0]) {
      setValue("type", allowedTypeIds[0]);
      setValue("unit", "");
      setValue("costCenter", "");
      setValue("category", "");
    }
  }, [isTypeFixed, allowedTypeIds]); // Removed formType and setValue to prevent infinite loop

  // Filter options based on selections
  const filteredUnits = safeUnits
    .filter(u => allowedTypeIds.includes(u.typeId)) // ✅ Standardized field name
    .filter(u => !formType || u.typeId === formType) // ✅ Standardized field name
    .filter(u => roles.includes("admin") || assignedUnits.includes(u.id));

  const typeOptions = types
    .filter(t => allowedTypeIds.includes(t.id))
    .map(t => ({ value: t.id, label: t.name }));
  const unitOptions = filteredUnits.map(u => ({ value: u.id, label: u.name }));

  // 🔍 DEBUG: Log cost center filtering
  // 🆕 SMART DEFAULTS: Pre-fill last-used cost center when type changes
  useEffect(() => {
    if (formType && !formCostCenter) {
      const lastUsedCostCenter = getLastUsedCostCenter(formType);
      const isValid = safeCostCenters.some(cc => 
        cc.id === lastUsedCostCenter && cc.typeId === formType
      );
      if (lastUsedCostCenter && isValid) {
        setValue("costCenter", lastUsedCostCenter);
      }
    }
  }, [formType, formCostCenter, safeCostCenters, setValue]);

  // 🆕 SMART DEFAULTS: Pre-fill last-used category when cost center changes
  useEffect(() => {
    if (formCostCenter && !formCategory) {
      const lastUsedCategory = getLastUsedCategory(formCostCenter);
      const isValid = safeCategories.some(cat => 
        cat.id === lastUsedCategory && cat.costCenterId === formCostCenter
      );
      if (lastUsedCategory && isValid) {
        setValue("category", lastUsedCategory);
      }
    }
  }, [formCostCenter, formCategory, safeCategories, setValue]);

  // 🔧 FIX: Enhanced cost center options using new hierarchy logic
  // Business Rule: Cost Centers are filtered by Type only (not Unit)
  // This ensures proper hierarchical relationships: Type → Units → Cost Centers → Categories
  const costCenterOptions = useMemo(() => {
    if (!formType) return [];

    // Use the new data hierarchy filtering logic
    const hierarchyOptions = getFilteredOptions(formType);
    if (hierarchyOptions.costCenters.length > 0) {
      return hierarchyOptions.costCenters;
    }

    // Fallback: Direct filtering if hierarchy fails
    return costCenters
      .filter(cc => cc.typeId === formType) // ✅ Standardized field name
      .map(cc => ({
        value: cc.id,
        label: cc.name,
        originalName: cc.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [formType, getFilteredOptions, costCenters]);

  // Handle category changes for name field and units
  useEffect(() => {
    const cat = safeCategories.find(c => c.id === formCategory);
    // Check needsNameField - this is the canonical field name in Category interface
    const nameRequired = cat?.needsNameField === true;
    const supportsUnits = !!cat?.supportsUnits;
    setShowName(nameRequired);
    setShowUnits(supportsUnits);
    setValue("showName", nameRequired);

    // If category doesn't support units and current mode is unit, switch to total
    const currentMode = getValues("mode");
    if (!supportsUnits && currentMode === "unit") {
      setValue("mode", "total", { shouldValidate: false });
    }
  }, [formCategory, safeCategories, setValue, getValues]); // Use safeCategories and include setValue/getValues

  // Filter items based on selected category (for stock nature)
  useEffect(() => {
    if (nature === 'stock' && formCategory) {
      const currentCategory = categoriesData.find(cat => cat.id === formCategory);
      if (currentCategory?.supportsItems) {
        const categoryItems = items.filter(item =>
          item.categoryId === formCategory && item.isActive !== false
        );
        setFilteredItems(categoryItems);
      } else {
        setFilteredItems([]);
      }
    } else {
      setFilteredItems([]);
    }
  }, [formCategory, categoriesData, items, nature]);

  // Determine unit label based on nature and selected item/category
  const getUnitLabel = () => {
    if (nature === 'stock' && selectedItem?.unitId) {
      // For stock items, use the unit from the selected item
      // Map unit IDs to display labels (same as in ItemsTab)
      const stockUnitOptions = [
        { value: 'kg', label: 'Kilogram (kg)' },
        { value: 'litre', label: 'Litre (L)' },
        { value: 'bags', label: 'Bags' },
        { value: 'ton', label: 'Ton (T)' },
        { value: 'can', label: 'Can' },
        { value: 'nos', label: 'Numbers (Nos)' },
      ];

      const unitOption = stockUnitOptions.find(u => u.value === selectedItem.unitId);
      return unitOption?.label || selectedItem.unitId || "Units";
    }
    // For other natures or when no item selected, use category unit label
    return selectedCategory?.unitLabel || "Units";
  };

  // Auto-calculation effect - use useMemo for stable calculation
  const calculatedAmount = useMemo(() => {
    return calculateAmount({
      mode: modeWatch,
      qty: qtyWatch,
      rate: rateWatch,
      gross: grossWatch,
      tax: taxWatch,
      gstAmount: gstAmountWatch,
      enableGst: enableGstWatch,
      supportsUnits: true, // Always allow both modes
    });
  }, [modeWatch, qtyWatch, rateWatch, grossWatch, taxWatch, gstAmountWatch, enableGstWatch]);

  useEffect(() => {
    const amountString = calculatedAmount ? String(calculatedAmount) : "";
    const currentAmount = watch("amount");

    // Only update if the calculated amount is different and not empty
    if (amountString && amountString !== currentAmount && calculatedAmount !== 0) {
      setValue("amount", amountString);
    }
  }, [calculatedAmount]); // Removed setValue to prevent infinite loop

  // Sync date range state (with proper null checks to prevent infinite loops)
  useEffect(() => {
    if (dateFromWatch && !dateFromValue) {
      setDateFromValue(new Date(dateFromWatch));
    }
    if (dateToWatch && !dateToValue) {
      setDateToValue(new Date(dateToWatch));
    }
  }, [dateFromWatch, dateToWatch]); // Removed dateFromValue and dateToValue to prevent infinite loop

  // Date constraints
  const todayStr = new Date().toISOString().split("T")[0];
  const minDateStr = roles.includes("admin")
    ? ""
    : new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  // Submit handler
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Handle bill parsed data
  const handleBillParsed = (matchedData: MatchedBillData, file: File) => {
    setParsedBillData(matchedData);
    setUploadedBillFile(file);
    setBillReviewOpen(true);
    setShowBillUpload(false);
  };

  // Handle expense creation from parsed bill
  const handleBillSubmit = async (formData: any, keepAttachment: boolean, file: File | null) => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      // Build expense entry from bill data
      const entry = {
        ...formData,
        date: new Date(formData.date),
        createdBy: user?.email || "",
        updatedBy: user?.email || "",
        approvalStatus: (roles.includes("admin") || roles.includes("approver")) ? "approved" : "pending",
        source: "bill_upload", // Track that this came from bill parsing
      };

      // Create the expense first
      const docRef = await postExpense(entry);

      // Upload attachment if requested
      if (keepAttachment && file && docRef?.id) {
        try {
          await uploadExpenseAttachment(file, docRef.id);
        } catch (uploadError) {
          console.error("Failed to upload attachment:", uploadError);
          // Don't fail the whole operation if attachment upload fails
        }
      }

      setSuccess("Expense created from bill!");
      toast.success("Expense created from bill!", { autoClose: 2000 });
      setBillReviewOpen(false);
      setParsedBillData(null);
      setUploadedBillFile(null);
    } catch (err: any) {
      console.error("Error creating expense from bill:", err);
      setError(err.message || "Error creating expense from bill");
      toast.error(err.message || "Error creating expense from bill");
    }
    setLoading(false);
  };

  // Prepare entities for bill matching
  const billMatchEntities = useMemo(() => ({
    types: types.map(t => ({ id: t.id, name: t.name })),
    units: safeUnits.map(u => ({ id: u.id, name: u.name, typeId: u.typeId })),
    costCenters: safeCostCenters.map(c => ({ id: c.id, name: c.name, typeId: c.typeId })),
    categories: safeCategories.map(c => ({ id: c.id, name: c.name, costCenterId: c.costCenterId })),
    parties: [], // Will be populated from shops collection if available
  }), [types, safeUnits, safeCostCenters, safeCategories]);

  const onSubmit = async (data: any) => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const entry = {
        typeId: data.type,
        type: types.find(t => t.id === data.type)?.name || "",
        unitId: data.unit,
        unit: safeUnits.find(u => u.id === data.unit)?.name || "",
        costCenterId: data.costCenter,
        costCenter: safeCostCenters.find(c => c.id === data.costCenter)?.name || "",
        categoryId: data.category,
        category: safeCategories.find(c => c.id === data.category)?.name || "",
        shop: toTitleCase(data.shop || ""), // Deprecated: kept for backwards compatibility
        partyName: data.partyName ? toTitleCase(data.partyName) : undefined,
        partyType: data.partyType || undefined,
        partyId: data.partyId || undefined,
        amount: Number(data.amount),
        // Persist quantity/rate when user selects unit mode, regardless of category.supportsUnits
        ...(modeWatch === "unit"
          ? {
            quantity: Number(data.quantity || 0),
            rate: Number(data.rate || 0),
            mode: "unit",
          }
          : {
            grossAmount: Number(data.gross || 0),
            mode: "total",
          }),
        ...(data.enableGst && data.gstAmount
          ? { taxAmount: Number(data.gstAmount) }
          : {}),
        date: new Date(data.date),
        ...(showDateRange && data.dateFrom && data.dateTo
          ? {
            dateFrom: new Date(data.dateFrom),
            dateTo: new Date(data.dateTo),
          }
          : {}),
        description: toTitleCase(data.description || ""),
        ...(showName ? { name: toTitleCase(data.name || "") } : {}),

        // Nature-specific fields
        ...(nature === "capital"
          ? {
            itemName: toTitleCase(data.itemName || ""),
            isConsumable: data.isConsumable || false,
            unitsSpent: data.unitsSpent ? Number(data.unitsSpent) : undefined,
            balance: data.balance ? Number(data.balance) : undefined,
          }
          : {}),

        // Stock-specific fields
        ...(nature === "stock"
          ? {
            itemId: selectedItem?.id || (data.stockItem || undefined),
            itemName: selectedItem?.name || (data.itemName ? toTitleCase(data.itemName) : undefined),
            itemUnitId: selectedItem?.unitId || undefined,
          }
          : {}),

        ...(nature === "payroll"
          ? {
            payoutMode: data.payoutMode || undefined,
            payoutFrequency: data.payoutFrequency || undefined,
          }
          : {}),

        ...(nature === "admin-finance"
          ? {
            recurring: data.recurring || false,
            dueDate: data.dueDate ? new Date(data.dueDate) : undefined,
            invoiceNo: data.invoiceNo || "",
          }
          : {}),

        ...(roles.includes("admin") || roles.includes("approver")) ? { approvalComments: data.approvalComments } : {},
        nature: nature as any,
        approvalStatus: (roles.includes("admin") || roles.includes("approver")) ? data.approvalStatus : "pending",
        createdBy: user?.email || "",
        updatedBy: user?.email || ""
      };

      await postExpense(entry);
      
      // 🆕 SMART DEFAULTS: Save last-used values for next time
      saveLastUsedValues({
        type: data.type,
        costCenter: data.costCenter,
        category: data.category,
        shop: data.shop
      });
      
      setSuccess("Expense saved!");
      toast.success("Expense saved!", { autoClose: 2000 });
      setTimeout(() => {
        reset({
          type: isTypeFixed ? allowedTypeIds[0] || "" : "",
          unit: "",
          costCenter: "",
          category: "",
          shop: "",
          amount: "",
          quantity: "",
          rate: "",
          gross: "",
          taxAmount: "",
          gstAmount: "",
          enableGst: false,
          date: "",
          dateFrom: "",
          dateTo: "",
          description: "",
          name: "",
          showName: false,
          stockItem: "", // Add stockItem field
          approvalStatus: "pending",
          approvalComments: "",
          itemName: "",
          isConsumable: false,
          unitsSpent: "",
          balance: "",
          payoutMode: undefined,
          payoutFrequency: undefined,
          recurring: false,
          dueDate: "",
          invoiceNo: "",
          nature: nature,
          // Maintain default mode for stock after reset
          mode: nature === 'stock' ? 'unit' : 'total',
          attachment: null,
        });
      }, 2000);
    } catch (err: any) {
      console.error("Expense submission error:", err);
      let errorMessage = "Error saving entry";
      if (err.code === 'permission-denied') {
        errorMessage = "Permission denied. Please check your user permissions and try again.";
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      toast.error(errorMessage);
    }
    setLoading(false);
  };

  const formRef = useRef<HTMLFormElement>(null);
  const onError = (formErrors: Record<string, unknown>) => {
    // Show user-friendly error message
    const errorMessages = Object.entries(formErrors).map(([field, error]: [string, any]) => {
      const message = error?.message || 'Validation error';
      return `${field}: ${message}`;
    });

    toast.error(`Form validation failed:\n${errorMessages.slice(0, 3).join('\n')}${errorMessages.length > 3 ? '\n...' : ''}`);

    const first = Object.keys(formErrors)[0];
    if (first && formRef.current) {
      const el = formRef.current.querySelector(`[name="${first}"]`) as HTMLElement | null;
      if (el) {
        (el as HTMLElement).focus();
        if (typeof (el as any).scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  };

  // Loading state
  if (mastersLoading) {
    return (
      <div className="p-4 text-center text-gray-600">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
        <p>Loading master data...</p>
      </div>
    );
  }

  // Error state
  if (mastersError) {
    return (
      <div className="p-4 text-center text-ds-status-error-main">
        <p>Failed to load master data.</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Reload page
        </button>
      </div>
    );
  }

  // Data validation check - only show after loading is complete
  if (!mastersLoading && !mastersError && (!types.length || !safeUnits.length || !safeCostCenters.length || !safeCategories.length)) {
    return (
      <div className="p-4 text-center text-ds-status-error-main">
        <p>No master data available. Please configure master data in Settings.</p>
        {!user && <p className="text-xs mt-2 text-gray-500">Waiting for authentication...</p>}
        <details className="mt-4 text-left text-xs">
          <summary className="cursor-pointer text-gray-600">Debug Info</summary>
          <pre className="mt-2 bg-gray-100 p-2 rounded">
            {JSON.stringify({
              user: !!user,
              mastersLoading,
              mastersError,
              typesCount: types.length,
              unitsCount: safeUnits.length,
              costCentersCount: safeCostCenters.length,
              categoriesCount: safeCategories.length,
            }, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="max-w-7xl mx-auto relative px-4 sm:px-6 lg:px-8">
        
        {/* 🆕 LEVEL 1: Business Type Selection Tabs (Mill/Vehicle) */}
        <BusinessTypeTabs
          activeType={businessType}
          onTypeChange={handleBusinessTypeChange}
          className="mb-6"
        />

        {/* 🆕 LEVEL 2: Expense Nature Selection Tabs (Routine/Stock/etc.) */}
        <NatureTabs
          businessType={businessType}
          activeNature={nature}
          onNatureChange={handleNatureChange}
          className="mb-6"
        />

        {/* Bill Upload Section */}
        <div className="mb-6">
          {!showBillUpload ? (
            <button
              type="button"
              onClick={() => setShowBillUpload(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg text-blue-700 hover:from-blue-100 hover:to-indigo-100 transition-all shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="font-medium">Scan Bill to Auto-Fill</span>
              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">AI</span>
            </button>
          ) : (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Upload Bill for Auto-Fill
                </h3>
                <button
                  type="button"
                  onClick={() => setShowBillUpload(false)}
                  className="text-blue-600 hover:text-blue-800 p-1"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <BillUpload
                onParsed={handleBillParsed}
                onError={(error) => toast.error(error)}
                disabled={loading}
                entities={billMatchEntities}
              />
            </div>
          )}
        </div>

        <form
          ref={formRef}
          onSubmit={handleSubmit(onSubmit, onError)}
          autoComplete="off"
          className="space-y-6"
        >
          {/* Main Content Grid */}
          <div className={`grid gap-6 ${effectiveLayout === 'single-column' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>

            {/* Left Column */}
            <div className="space-y-6">

              {/* Section 1: Expense Metadata */}
              <div className="bg-bg-surface rounded-xl border border-border-default shadow-sm p-6">
                <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <div className="w-6 h-6 bg-ds-primary-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-ds-primary-600 text-sm font-bold">1</span>
                  </div>
                  Expense Metadata
                </h2>

                {/* Two-column responsive grid - stacks to single column on mobile */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Unit - Spans full width when radio buttons are shown (for better layout) */}
                  <div className={`space-y-1 ${unitOptions.length <= 5 && unitOptions.length > 1 ? 'md:col-span-2' : ''}`}>
                    {unitOptions.length <= 5 && unitOptions.length > 1 ? (
                      <Controller
                        name="unit"
                        control={control}
                        render={({ field }) => (
                          <RadioGroup
                            label="Business Unit"
                            options={unitOptions.map(opt => ({
                              value: opt.value,
                              label: opt.label
                            }))}
                            value={field.value}
                            onChange={field.onChange}
                            disabled={!formType}
                            name="unit"
                            required
                            error={errors.unit ? getFieldErrorMessage('unit', errors.unit) : undefined}
                          />
                        )}
                      />
                    ) : (
                      <>
                        <label htmlFor="unit" className="block text-sm font-medium text-text-secondary">
                          Business Unit <span className="text-ds-status-error-main">*</span>
                        </label>
                        <select
                          id="unit"
                          {...register("unit")}
                          disabled={!formType}
                          className="w-full border border-border-muted bg-bg-surface px-3 py-2.5 rounded-lg outline-none focus:border-ds-primary-500 focus:ring-2 focus:ring-ds-primary-500/20 text-sm transition-colors disabled:bg-bg-muted disabled:text-text-muted"
                        >
                          <option value="">Select business unit...</option>
                          {unitOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.unit && (
                          <p className="text-xs text-ds-status-error-main mt-1 flex items-center gap-1" role="alert">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {getFieldErrorMessage('unit', errors.unit)}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Cost Center */}
                  <div className="space-y-1">
                    <label htmlFor="costCenter" className="block text-sm font-medium text-text-secondary">
                      Cost Center <span className="text-ds-status-error-main">*</span>
                    </label>
                    <select
                      id="costCenter"
                      {...register("costCenter")}
                      disabled={!formType}
                      className="w-full border border-border-muted bg-bg-surface px-3 py-2.5 rounded-lg outline-none focus:border-ds-primary-500 focus:ring-2 focus:ring-ds-primary-500/20 text-sm transition-colors disabled:bg-bg-muted disabled:text-text-muted"
                    >
                      <option value="">Select cost center...</option>
                      {costCenterOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.costCenter && (
                      <p className="text-xs text-ds-status-error-main mt-1 flex items-center gap-1" role="alert">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {getFieldErrorMessage('costCenter', errors.costCenter)}
                      </p>
                    )}
                  </div>

                  {/* Category */}
                  <Controller
                    control={control}
                    name="category"
                    render={({ field, fieldState }) => (
                      <div className="space-y-1">
                        <label htmlFor="category" className="block text-sm font-medium text-text-secondary">
                          Expense Category <span className="text-ds-status-error-main">*</span>
                        </label>
                        <GroupedCategoryDropdown
                          typeId={formType}
                          costCenterId={formCostCenter}
                          nature={nature as any}
                          value={field.value}
                          onSelect={(categoryId, categoryMeta) => {
                            field.onChange(categoryId);
                            if (categoryMeta.category.needsNameField) {
                              setValue("name", "");
                            }
                          }}
                          placeholder="" 
                          disabled={!formCostCenter}
                        />
                        {!fieldState.error && (
                          <p className="text-xs text-text-secondary mt-1">
                            {getFieldHelperText('category')}
                          </p>
                        )}
                        {fieldState.error && (
                          <p className="text-xs text-ds-status-error-main mt-1 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z" clipRule="evenodd"/>
                            </svg>
                            {getFieldErrorMessage('category', fieldState.error)}
                          </p>
                        )}
                      </div>
                    )}
                  />

                  {/* Items Dropdown - Only for stock nature with supportsItems categories */}
                  {nature === 'stock' && selectedCategory?.supportsItems && (
                    <Controller
                      control={control}
                      name="stockItem"
                      render={({ field, fieldState }) => (
                        <div className="space-y-1">
                          <label htmlFor="stockItem" className="block text-sm font-medium text-text-secondary">
                            Select Item <span className="text-ds-status-error-main">*</span>
                          </label>
                          <SearchableDropdown
                            options={filteredItems.map(item => ({
                              value: item.id || '',
                              label: `${item.name} (${item.unitId || 'unit'})`
                            }))}
                            value={field.value || ''}
                            onSelect={(value) => {
                              field.onChange(value);
                              // Auto-populate item name from selected item
                              const selectedItem = filteredItems.find(item => item.id === value);
                              if (selectedItem) {
                                setValue('itemName', selectedItem.name);
                              }
                            }}
                            placeholder=""
                            disabled={loading || filteredItems.length === 0}
                          />
                          {!fieldState.error && filteredItems.length > 0 && (
                            <p className="text-xs text-text-secondary mt-1">
                              Select a predefined item from the list
                            </p>
                          )}
                          {filteredItems.length === 0 && formCategory && (
                            <p className="text-warning-600 text-xs mt-1 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                              </svg>
                              No items configured for this category. Add items in Settings → Items.
                            </p>
                          )}
                          {fieldState.error && (
                            <p className="text-xs text-ds-status-error-main mt-1 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z" clipRule="evenodd"/>
                              </svg>
                              {getFieldErrorMessage('stockItem', fieldState.error)}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  )}

                  {/* Expense Date */}
                  <div className="space-y-1">
                    <label htmlFor="date" className="block text-sm font-medium text-text-secondary">
                      Expense Date <span className="text-ds-status-error-main">*</span>
                    </label>
                    <input
                      id="date"
                      type="date"
                      max={todayStr}
                      min={minDateStr}
                      {...register("date")}
                      className="w-full border border-border-muted bg-bg-surface px-3 py-2.5 rounded-lg outline-none focus:border-ds-primary-500 focus:ring-2 focus:ring-ds-primary-500/20 text-sm transition-colors"
                    />
                    {errors.date && (
                      <p className="text-xs text-ds-status-error-main mt-1 flex items-center gap-1" role="alert">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {getFieldErrorMessage('date', errors.date)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Date Range - Full Width */}
                {showDateRange && (
                  <div className="mt-4">
                    <Controller
                      control={control}
                      name="dateFrom"
                      render={({ field, fieldState }) => (
                        <DateRangePicker
                          dateFrom={dateFromValue}
                          dateTo={dateToValue}
                          onDateFromChange={(date) => {
                            setDateFromValue(date);
                            field.onChange(date ? date.toISOString().split('T')[0] : '');
                          }}
                          onDateToChange={(date) => {
                            setDateToValue(date);
                            setValue('dateTo', date ? date.toISOString().split('T')[0] : '');
                          }}
                          error={fieldState.error?.message || errors.dateTo?.message as string}
                          required
                          disabled={loading}
                        />
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Section 2: Party Details */}
              <div className="bg-bg-surface rounded-xl border border-border-default shadow-sm p-6">
                <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <div className="w-6 h-6 bg-ds-status-success-light rounded-full flex items-center justify-center mr-3">
                    <span className="text-ds-status-success-main text-sm font-bold">2</span>
                  </div>
                  Party Details
                </h2>

                {/* Two-column responsive grid - description spans full width */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Dynamic Party Input - half width on desktop */}
                  {visibleFields.has("shop") && (
                    <div className="space-y-1">
                      <label htmlFor={`party-input-${nature}`} className="block text-sm font-medium text-text-secondary">
                        {nature === 'routine' ? 'Shop Name' :
                          nature === 'stock' || nature === 'capital' ? 'Vendor Name' :
                            nature === 'admin-finance' ? 'Office/Bank' :
                              nature === 'payroll' ? 'Labour Group' : 'Party Name'} <span className="text-ds-status-error-main">*</span>
                      </label>
                      <Controller
                        control={control}
                        name="shop"
                        render={({ field, fieldState }) => (
                          <DynamicPartyInput
                            nature={nature}
                            value={field.value || ""}
                            onChange={field.onChange}
                            onPartyChange={(party: PartyData) => {
                              // Update unified party system fields
                              setValue('partyName', party.name);
                              setValue('partyType', party.type);
                              setValue('partyId', party.id || '');
                            }}
                            error={fieldState.error?.message}
                            required={true}
                            disabled={loading}
                            hideInternalLabel={true}
                          />
                        )}
                      />
                      <div className="text-xs text-text-muted">
                        Type to search existing {nature === 'routine' ? 'shops' :
                          nature === 'stock' || nature === 'capital' ? 'vendors' :
                            nature === 'admin-finance' ? 'offices' :
                              nature === 'payroll' ? 'labour groups' : 'parties'} or add new
                      </div>
                    </div>
                  )}

                  {/* Asset ID/Name Field - half width on desktop */}
                  <div className="space-y-1">
                    <label htmlFor="name" className="block text-sm font-medium text-text-secondary">
                      Asset ID/Name {showName ? <span className="text-ds-status-error-main">*</span> : <span className="text-text-muted text-sm">(Optional)</span>}
                    </label>
                    <input
                      id="name"
                      type="text"
                      {...register("name")}
                      disabled={!showName}
                      className={`w-full border-2 px-3 py-2.5 rounded-lg outline-none text-sm transition-colors ${showName
                          ? errors.name
                            ? 'border-ds-status-error-main bg-bg-surface focus:border-ds-status-error-main focus:ring-2 focus:ring-ds-status-error-main/20'
                            : 'border-warning-300 bg-warning-50 focus:border-warning-500 focus:ring-2 focus:ring-warning-500/20'
                          : 'border-border-default bg-bg-muted text-text-placeholder cursor-not-allowed'
                        }`}
                      maxLength={20}
                      aria-invalid={errors.name ? "true" : "false"}
                      aria-describedby={errors.name ? "name-error" : "name-help"}
                      {...nameSecurity}
                    />
                    {!errors.name && showName && (
                      <p id="name-help" className="text-xs text-text-secondary">
                        {getFieldHelperText('assetId')}
                      </p>
                    )}
                    {!showName && (
                      <p className="text-xs text-text-muted">
                        Asset tracking not required for this category
                      </p>
                    )}
                    {errors.name && showName && (
                      <p id="name-error" className="text-xs text-ds-status-error-main flex items-center gap-1" role="alert">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z" clipRule="evenodd"/>
                        </svg>
                        {getFieldErrorMessage('assetId', errors.name)}
                      </p>
                    )}
                  </div>

                  {/* Description Field - Spans full width for better text entry */}
                  {visibleFields.has("description") && (
                    <div className="space-y-1 md:col-span-2">
                      <label htmlFor="description" className="block text-sm font-medium text-text-secondary">
                        Expense Description {requiredFields.has("description") && <span className="text-ds-status-error-main">*</span>}
                      </label>
                      <textarea
                        id="description"
                        {...register("description")}
                        className={`w-full border-2 bg-bg-surface px-3 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-ds-primary-500/20 text-sm transition-colors resize-none h-20 ${
                          errors.description ? 'border-ds-status-error-main focus:border-ds-status-error-main' : 'border-border-muted focus:border-ds-primary-500'
                        }`}
                        maxLength={INPUT_LIMITS.DESCRIPTION}
                        aria-invalid={errors.description ? "true" : "false"}
                        aria-describedby={errors.description ? "description-error" : "description-help"}
                        {...descriptionSecurity}
                      />
                      {!errors.description && (
                        <p id="description-help" className="text-xs text-text-secondary">
                          {getFieldHelperText('description')} ({formDescription?.length || 0}/{INPUT_LIMITS.DESCRIPTION} characters)
                        </p>
                      )}
                      {errors.description && (
                        <p id="description-error" className="text-xs text-ds-status-error-main flex items-center gap-1" role="alert">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z" clipRule="evenodd"/>
                          </svg>
                          {getFieldErrorMessage('description', errors.description)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Right Column */}
            <div className="space-y-6">

              {/* Section 3: Amount Details */}
              <div className="bg-bg-surface rounded-xl border border-border-default shadow-sm p-6">
                <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <div className="w-6 h-6 bg-info-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-info-600 text-sm font-bold">3</span>
                  </div>
                  Amount Details
                </h2>

                <UnifiedAmountModule
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  disabled={loading}
                  unitLabel={getUnitLabel()}
                  showQuantityMode={showUnits}
                />
              </div>

              {/* Module-Specific Fields */}
              {(nature === "capital" || nature === "payroll" || nature === "admin-finance" || nature === "stock") && (
                <div className="bg-bg-surface rounded-xl border border-border-default shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                    <div className="w-6 h-6 bg-info-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-info-600 text-sm font-bold">4</span>
                    </div>
                    {`${nature.charAt(0).toUpperCase() + nature.slice(1)} Details`}
                  </h2>
                  <ModuleSpecificFields
                    type={nature}
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    control={control}
                    errors={errors}
                    disabled={loading}
                  />
                </div>
              )}

            </div>
          </div>

          {/* Section 4: Optional Details - Collapsible */}
          <div className="bg-bg-surface rounded-xl border border-border-default shadow-sm">
            <button
              type="button"
              onClick={() => setShowOptionalDetails(!showOptionalDetails)}
              className="w-full p-6 flex items-center justify-between text-left hover:bg-bg-muted transition-colors"
            >
              <h2 className="text-lg font-semibold text-text-primary flex items-center">
                <div className="w-6 h-6 bg-bg-subtle rounded-full flex items-center justify-center mr-3">
                  <span className="text-text-secondary text-sm font-bold">5</span>
                </div>
                Optional Details
              </h2>
              {showOptionalDetails ? (
                <svg className="w-5 h-5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>

            {showOptionalDetails && (
              <div className="px-6 pb-6 border-t border-gray-100">
                <div className={`grid gap-6 mt-6 ${effectiveLayout === 'single-column' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                  {/* File Upload - Only file upload remains in optional details */}
                  <div className="col-span-1 md:col-span-2">
                    <Controller
                      control={control}
                      name="attachment"
                      render={({ field, fieldState }) => (
                        <FileUpload
                          files={field.value || []}
                          onFilesChange={field.onChange}
                          error={fieldState.error?.message}
                          disabled={loading}
                          required={false}
                        />
                      )}
                    />
                  </div>

                  {/* Approval Fields for Admin/Approver */}
                  {(roles.includes("admin") || roles.includes("approver")) && (
                    <>
                      <div className="space-y-1">
                        <label htmlFor="approvalStatus" className="block text-sm font-medium text-text-secondary">
                          Approval Status
                        </label>
                        <select
                          id="approvalStatus"
                          {...register("approvalStatus")}
                          className="w-full border border-border-muted bg-bg-surface px-3 py-2.5 rounded-lg outline-none focus:border-ds-primary-500 focus:ring-2 focus:ring-ds-primary-500/20 text-sm transition-colors"
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>

                      <div className="col-span-1 md:col-span-2 space-y-1">
                        <label htmlFor="approvalComments" className="block text-sm font-medium text-text-secondary">
                          Approval Comments <span className="text-text-muted text-sm">(Optional)</span>
                        </label>
                        <textarea
                          id="approvalComments"
                          {...register("approvalComments")}
                          className="w-full border-2 border-border-muted bg-bg-surface px-3 py-2.5 rounded-lg outline-none focus:border-ds-primary-500 focus:ring-2 focus:ring-ds-primary-500/20 text-sm transition-colors resize-none h-20"
                          aria-describedby="approvalComments-help"
                        />
                        <p id="approvalComments-help" className="text-xs text-text-secondary">
                          Add any feedback or notes about this expense (optional)
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sticky Action Buttons */}
          <div className="sticky bottom-0 bg-bg-surface border-t border-border-default shadow-lg rounded-t-xl p-4">
            <div className="flex flex-col sm:flex-row gap-3 justify-end max-w-7xl mx-auto">
              <button
                type="button"
                onClick={async () => {
                  const confirmed = await confirm({
                    title: 'Clear Form',
                    message: 'Clear all fields and start over?',
                    confirmText: 'Clear',
                    cancelText: 'Cancel',
                    variant: 'warning'
                  });
                  
                  if (confirmed) {
                    reset({
                      type: isTypeFixed ? allowedTypeIds[0] || "" : "",
                      unit: "",
                      costCenter: "",
                      category: "",
                      shop: "",
                      amount: "",
                      quantity: "",
                      rate: "",
                      gross: "",
                      taxAmount: "",
                      gstAmount: "",
                      enableGst: false,
                      date: "",
                      dateFrom: "",
                      dateTo: "",
                      description: "",
                      name: "",
                      showName: false,
                      stockItem: "",
                      approvalStatus: "pending",
                      approvalComments: "",
                      itemName: "",
                      isConsumable: false,
                      unitsSpent: "",
                      balance: "",
                      payoutMode: undefined,
                      payoutFrequency: undefined,
                      recurring: false,
                      dueDate: "",
                      invoiceNo: "",
                      nature: nature as any,
                      mode: nature === 'stock' ? 'unit' : 'total',
                      attachment: null,
                    });
                  }
                }}
                disabled={loading}
                className="text-sm text-text-secondary hover:text-text-primary underline disabled:opacity-50"
              >
                Clear all fields
              </button>
              <PrimaryButton
                type="submit"
                loading={loading}
                disabled={isSubmitting || loading}
                className="btn-enterprise"
              >
                Record {nature === 'stock' ? 'Stock' : 'Financial'} Expense →
              </PrimaryButton>
            </div>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4">
              <div className="text-ds-status-error-main text-sm">{error}</div>
            </div>
          )}
          {success && (
            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
              <div className="text-success-700 text-sm">{success}</div>
            </div>
          )}
        </form>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog {...dialogProps} />

      {/* Bill Review Modal */}
      <BillReviewModal
        open={billReviewOpen}
        matchedData={parsedBillData}
        billFile={uploadedBillFile}
        onClose={() => {
          setBillReviewOpen(false);
          setParsedBillData(null);
          setUploadedBillFile(null);
        }}
        onSubmit={handleBillSubmit}
        types={types}
        units={safeUnits.map(u => ({ id: u.id, name: u.name, type: u.typeId || '' }))}
        costCenters={safeCostCenters.map(c => ({ id: c.id, name: c.name, type: c.typeId || '' }))}
        categories={safeCategories.map(c => ({
          id: c.id,
          name: c.name,
          type: c.typeId || '',
          costCenter: c.costCenterId || '',
          needsNameField: c.needsNameField,
          supportsUnits: c.supportsUnits,
        }))}
        parties={[]}
      />
    </FormProvider>
  );
};

export default ModernDataEntryForm;